import { useState, useEffect, useRef, useCallback } from 'react';

// --- RIGOROUS PHYSICS CONSTANTS ---
// Lambda (λ): Harmonic Damping Factor for mouse parallax (simulated friction)
const HARMONIC_DAMPING_FACTOR = 0.07; 
// Alpha (α): Scroll Influence Factor (how much vertical scroll affects depth)
const SCROLL_INFLUENCE_FACTOR = 0.5; 

const SystemicCreationPage = ({ onClose, animationStage, onNavigate }) => {
  const [contentStage, setContentStage] = useState('entering');
  const [activeService, setActiveService] = useState(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollFactor, setScrollFactor] = useState(0); 

  // Refs for LERP Damping (Simulated Friction)
  const dampedMouseX = useRef(0);
  const dampedMouseY = useRef(0);
  const animationFrameRef = useRef(null);
  const pageRef = useRef(null);
  
  // Services with elevated, rigorous terminology
  const services = [
    {
      id: 'tech-consultation',
      title: "Architecture Synthesis",
      description: "Strategic technology guidance for digital transformation and innovation",
      icon: "⟁", 
      gradient: "from-cyan-500 to-blue-500",
      particleColor: "rgba(6, 182, 212, 0.1)",
      features: ["Blueprinting", "Topology Mapping", "Resilience Analysis", "Cloud Native Strategy"],
      capabilities: ["Enterprise Solutions", "System Integration", "Process Optimization"],
      stats: { uptime: "99.99%", latency: "<50ms", reliability: "98%" },
      techStack: ["TOGAF", "ITIL", "Archimate", "DevOps"],
      caseStudy: "Optimizing enterprise architecture for zero-downtime deployment"
    },
    {
      id: 'software-development',
      title: "Algorithmic Engineering", 
      description: "Building scalable, robust applications for web, mobile, and enterprise",
      icon: "𝜦", 
      gradient: "from-purple-500 to-fuchsia-500",
      particleColor: "rgba(168, 85, 247, 0.1)",
      features: ["Non-Blocking I/O", "Microservice Design", "Domain-Driven Design (DDD)", "Quantum-Resistant Encryption"],
      capabilities: ["Web Applications", "Mobile Solutions", "Enterprise Systems"],
      stats: { efficiency: "98%", speed: "10x", precision: "5 sig. figs" },
      techStack: ["React", "Vue", "Node.js", "Python", "Rust", "Go"],
      caseStudy: "Implemented a fully parallelized, event-driven data pipeline"
    },
    {
      id: 'data-analytics',
      title: "Predictive Intelligence",
      description: "Transforming raw data into actionable business intelligence",
      icon: "⎔", 
      gradient: "from-orange-500 to-amber-500", 
      particleColor: "rgba(249, 115, 22, 0.1)",
      features: ["Bayesian Inference", "Neural Net Training", "Generative Modeling", "Data Topology Mapping"],
      capabilities: ["Data Pipelines", "Predictive Models", "Dashboard Development"],
      stats: { accuracy: "96.5%", recall: "95.8%", f1_score: "96.1" },
      techStack: ["Python", "TensorFlow", "PyTorch", "Spark", "SQL"],
      caseStudy: "Achieved 34% sales increase through deep learning model deployment"
    },
    {
      id: 'cloud-iot',
      title: "Distributed Ecosystems",
      description: "Scalable cloud solutions and connected device ecosystems",
      icon: "Ψ", 
      gradient: "from-emerald-500 to-teal-500",
      particleColor: "rgba(16, 185, 129, 0.1)", 
      features: ["Serverless Functions", "Edge Computing Topology", "Decentralized Mesh Networks", "Real-time Monitoring"],
      capabilities: ["AWS/GCP/Azure", "Connected Systems", "Smart Automation"],
      stats: { availability: "99.999%", throughput: "10k/s", footprint: "1/5th" },
      techStack: ["Kubernetes", "Docker", "MQTT", "Grafana", "Prometheus"],
      caseStudy: "Reduced infrastructure costs by 60% with full containerization strategy"
    }
  ];

  // --- Viewport, Mouse, and Scroll Tracking (Physics Engine) ---
  useEffect(() => {
    const updateViewport = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  // Scroll Depth Calculation
  useEffect(() => {
    const handleScroll = () => {
      if (pageRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = pageRef.current;
        const maxScroll = scrollHeight - clientHeight;
        setScrollFactor(maxScroll > 0 ? scrollTop / maxScroll : 0);
      }
    };
    const ref = pageRef.current;
    if (ref) {
      ref.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (ref) {
        ref.removeEventListener('scroll', handleScroll);
      }
    };
  }, [viewport]); 

  // Harmonic Damping Loop
  const animate = useCallback(() => {
    const targetX = (mousePosition.x / viewport.width - 0.5) * 40;
    const targetY = (mousePosition.y / viewport.height - 0.5) * 40;

    dampedMouseX.current += (targetX - dampedMouseX.current) * HARMONIC_DAMPING_FACTOR;
    dampedMouseY.current += (targetY - dampedMouseY.current) * HARMONIC_DAMPING_FACTOR;

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [mousePosition.x, mousePosition.y, viewport.width, viewport.height]); 

  useEffect(() => {
      if (viewport.width > 0) {
          animationFrameRef.current = requestAnimationFrame(animate);
      }
      return () => cancelAnimationFrame(animationFrameRef.current);
  }, [animate, viewport.width]);

  // Content State Management
  useEffect(() => {
    if (animationStage === 'entering') {
      setContentStage('entering');
      const timer = setTimeout(() => setContentStage('visible'), 100);
      return () => clearTimeout(timer);
    } else if (animationStage === 'exiting') {
      setContentStage('exiting');
    }
  }, [animationStage]);

  const handleServiceClick = (serviceId) => {
    // Scroll to the clicked card to ensure it's fully in view when expanded
    setActiveService(prevActiveService => {
        if (prevActiveService === serviceId) {
            return null; // Collapse
        } else {
            // Optional: Find card element and scroll into view after state update
            setTimeout(() => {
                const cardElement = document.getElementById(`service-card-${serviceId}`);
                // Only scroll if the element exists and is outside the current viewport
                if (cardElement) {
                   const rect = cardElement.getBoundingClientRect();
                   if (rect.top < 0 || rect.bottom > window.innerHeight) {
                       cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                   }
                }
            }, 300);
            return serviceId; // Expand
        }
    });
  };

  // Physics values
  const smoothedX = dampedMouseX.current;
  const smoothedY = dampedMouseY.current;
  const zDepth = scrollFactor * SCROLL_INFLUENCE_FACTOR;


  return (
    <div 
      ref={pageRef}
      className="fixed inset-0 z-50 bg-slate-950 overflow-y-scroll"
    >
      {/* 1. THE WORMHOLE SINGULARITY BACKGROUND (Dynamic Vector Field) */}
      <div className="absolute inset-0 overflow-hidden perspective-bg">
        <div 
          className="absolute top-1/2 left-1/2 w-[200vw] h-[200vh] -translate-x-1/2 -translate-y-1/2 opacity-50 transition-transform duration-100 ease-out"
          style={{
            transform: `
                translate3d(${smoothedX * 0.3}px, ${smoothedY * 0.3}px, 0) 
                scale(${1 + zDepth * 0.5})
                rotateX(${zDepth * 10}deg)
            `,
            background: `radial-gradient(circle at 50% 50%, 
                rgba(0, 200, 255, 0.2) 0%,
                rgba(140, 0, 255, 0.1) 40%,
                transparent 70%
            )`,
            filter: `blur(100px) hue-rotate(${zDepth * 100}deg)`,
          }}
        >
            <div className="absolute inset-[10%] bg-white/5 rounded-full blur-3xl animate-pulse-slow"></div>
        </div>

        {/* Dynamic Grid System */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"
            style={{
              transform: `translate3d(${smoothedX * -0.1}px, ${smoothedY * -0.1}px, 0) scale(${1 - zDepth * 0.2})`
            }}
          ></div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/70">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
              <div className="flex items-center gap-3 text-white font-mono text-lg tracking-tight">
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  NIXQUE LAB
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="group px-6 py-3 rounded-xl border border-slate-700 text-white hover:bg-slate-800/50 transition-all duration-300 font-mono text-sm tracking-wide hover:border-cyan-400/50 hover:text-cyan-300 backdrop-blur-sm flex items-center gap-2"
            >
              <span>RETURN TO MAIN</span>
              <div className="group-hover:translate-x-1 transition-transform duration-200">
                ↗
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="relative z-10 pt-24 pb-32 min-h-screen">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-24 transition-all duration-1000 ${
            contentStage === 'entering' 
              ? 'opacity-0 transform translate-y-12 blur-sm' 
              : 'opacity-100 transform translate-y-0 blur-0'
          }`}>
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm mb-8">
              <span className="text-cyan-400 text-sm font-mono tracking-wide">
                VECTOR FIELD ANALYSIS: SERVICE TOPOLOGY
              </span>
            </div>
            
            <div className="relative">
              <h1 className="text-6xl lg:text-8xl font-bold mb-8 font-mono tracking-tighter">
                <span className="bg-gradient-to-br from-white via-cyan-100 to-blue-200 bg-clip-text text-transparent bg-size-200 bg-pos-0 hover:bg-pos-100 transition-all duration-1000">
                  SYSTEMIC CREATION
                </span>
              </h1>
            </div>
            
            <div className="flex justify-center mb-8">
              <div className="w-32 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shine"></div>
              </div>
            </div>
            
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              We engineer <span className="text-cyan-300 font-semibold">emergent digital architecture</span>, 
              deploy <span className="text-purple-300 font-semibold">self-optimizing algorithms</span>, and 
              chart <span className="text-amber-300 font-semibold">predictive data manifolds</span>.
            </p>
          </div>

          {/* 3. DYNAMIC RELATIONAL MANIFOLD (Staggered Grid Layout - FIXES OVERLAP) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-16 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                id={`service-card-${service.id}`}
                // The key fix for stagger: apply margin top to odd indices on large screens
                className={`transition-all duration-1000 ${
                    index % 2 !== 0 ? 'lg:mt-16' : ''
                }`}
              >
                <InteractiveServiceCard
                  service={service}
                  index={index}
                  isActive={activeService === service.id}
                  onClick={handleServiceClick}
                  contentStage={contentStage}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack and CTA Sections */}
      <section className="relative z-10 py-20 border-y border-slate-800/50 bg-slate-900/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-mono">
              TECHNOLOGY ECOSYSTEM
            </h2>
            <p className="text-slate-400">Built with the most advanced tools and platforms</p>
          </div>
          <TechStackMarquee />
        </div>
      </section>

      <section className="relative z-10 py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className={`transition-all duration-1000 ${
            contentStage === 'entering' 
              ? 'opacity-0 transform translate-y-8' 
              : 'opacity-100 transform translate-y-0'
          }`} style={{ transitionDelay: '1000ms' }}>
            
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 font-mono bg-gradient-to-br from-white to-slate-300 bg-clip-text text-transparent">
                Synthesize Your Trajectory
              </h2>
              <div className="w-20 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-400 mx-auto mb-6 rounded-full"></div>
              <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                Define the parameters for system initialization.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:gap-6 justify-center items-stretch max-w-2xl mx-auto">
              
              <button 
                onClick={() => window.open('mailto:contact@nixque.com?subject=New Project Inquiry - NIXQUE&body=Hello NIXQUE team,%0A%0AI would like to discuss a new project.%0A%0AProject Overview:%0A• %0A%0AGoals:%0A• %0A%0ATimeline:%0A• %0A%0ABudget:%0A• %0A%0ALooking forward to your response!', '_blank')}
                className="group relative bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 p-0.5 rounded-2xl md:rounded-3xl transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/25 flex-1 min-w-[280px]"
              >
                <div className="bg-slate-900 rounded-2xl md:rounded-3xl p-6 md:p-8 h-full backdrop-blur-sm">
                  <div className="text-cyan-400 text-2xl mb-3 group-hover:scale-110 transition-transform duration-300">
                    🚀
                  </div>
                  <h3 className="text-white font-mono font-bold text-lg md:text-xl mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-blue-400 transition-all duration-300">
                    INITIATE PROJECT
                  </h3>
                  <p className="text-slate-400 text-sm mb-4 group-hover:text-slate-300 transition-colors duration-300">
                    Submit a formal proposal for system implementation
                  </p>
                  <div className="flex items-center justify-center gap-2 text-cyan-400 text-sm font-mono">
                    <span>Execute Query</span>
                    <div className="group-hover:translate-x-1 transition-transform duration-300">→</div>
                  </div>
                </div>
                
                <div className="absolute inset-0 rounded-2xl md:rounded-3xl bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-sm"></div>
              </button>

              <button 
                onClick={() => window.open('tel:+254788979327', '_blank')}
                className="group relative border-2 border-slate-700 bg-slate-900/50 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-8 transition-all duration-500 hover:scale-105 hover:border-cyan-400/50 hover:bg-slate-800/30 flex-1 min-w-[280px]"
              >
                <div className="text-cyan-400 text-2xl mb-3 group-hover:scale-110 transition-transform duration-300">
                  💡
                </div>
                <h3 className="text-white font-mono font-bold text-lg md:text-xl mb-2 group-hover:text-cyan-300 transition-colors duration-300">
                    STRATEGY SESSION
                </h3>
                <p className="text-slate-400 text-sm mb-4 group-hover:text-slate-300 transition-colors duration-300">
                    Discuss strategic parameters and feasibility analysis
                </p>
                <div className="flex items-center justify-center gap-2 text-cyan-400 text-sm font-mono">
                  <span>Call Now</span>
                  <div className="group-hover:translate-x-1 transition-transform duration-300">→</div>
                </div>
                
                <div className="absolute inset-0 rounded-2xl md:rounded-3xl bg-cyan-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <Footer onNavigate={onNavigate} />

      <style jsx>{`
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes scanline {
          0% { background-position: 0 0; }
          100% { background-position: 0 100%; }
        }
        @keyframes pulse-slow {
            0%, 100% { opacity: 0.2; }
            50% { opacity: 0.4; }
        }
        
        .animate-shine {
          animation: shine 2s ease-in-out infinite;
        }
        .animate-pulse-slow {
            animation: pulse-slow 5s ease-in-out infinite;
        }
        .bg-size-200 {
          background-size: 200% 200%;
        }
        .bg-pos-0 {
          background-position: 0% 0%;
        }
        .bg-pos-100 {
          background-position: 100% 100%;
        }
        
        /* New Scanline Effect for Artistic Detail */
        .scanline-overlay {
            position: absolute;
            inset: 0;
            background: repeating-linear-gradient(
                0deg,
                rgba(0, 0, 0, 0.1) 0px,
                rgba(255, 255, 255, 0.05) 1px,
                transparent 2px,
                transparent 5px
            );
            mix-blend-mode: overlay;
            pointer-events: none;
            opacity: 0.5;
            animation: scanline 8s linear infinite;
        }
        /* Style for the Wormhole Singularity Container */
        .perspective-bg {
            perspective: 1000px;
        }
        
        /* Hypercube Card Styles */
        .hypercube-border {
            position: absolute;
            inset: 0;
            pointer-events: none;
            transition: all 0.5s ease;
        }
        .hypercube-border::before, .hypercube-border::after {
            content: '';
            position: absolute;
            border-radius: 0.75rem; /* Matches rounded-xl */
            border: 1px solid;
            transition: all 0.5s ease;
        }
        
        /* Inner Border 1: Skewed and Tilted for 3D depth */
        .hypercube-border::before {
            inset: 0.25rem;
            transform: skewX(-2deg) scale(0.99);
            opacity: 0.2;
        }
        
        /* Inner Border 2: Subtly glowing */
        .hypercube-border::after {
            inset: 0.5rem;
            transform: scale(0.97);
            opacity: 0.1;
        }

        /* Holographic Plasma Core Animation */
        @keyframes plasma-pulse {
            0%, 100% { box-shadow: 0 0 15px 5px var(--plasma-color); opacity: 0.4; }
            50% { box-shadow: 0 0 30px 10px var(--plasma-color); opacity: 0.8; }
        }
        .plasma-core {
            border-radius: 9999px;
            filter: blur(10px);
            animation: plasma-pulse 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// --- FRACTAL DATA NODE COMPONENT (Hypercube Styling) ---
const InteractiveServiceCard = ({ service, index, isActive, onClick, contentStage }) => {
  const cardRef = useRef(null);
  const [localMouse, setLocalMouse] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState('features');
  
  // Extract primary color from gradient for plasma core 
  const getPlasmaColor = (gradient) => {
      if (gradient.includes('cyan')) return 'rgba(6, 182, 212, 0.5)';
      if (gradient.includes('purple')) return 'rgba(168, 85, 247, 0.5)';
      if (gradient.includes('orange')) return 'rgba(249, 115, 22, 0.5)';
      if (gradient.includes('emerald')) return 'rgba(16, 185, 129, 0.5)';
      return 'rgba(255, 255, 255, 0.5)';
  };

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    
    // Normalized position relative to center (-1 to 1)
    const xRatio = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
    const yRatio = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
    
    // Non-Linear (CUBIC) tilt
    const tiltStrength = 15; 
    const x = xRatio * tiltStrength * xRatio * xRatio; 
    const y = yRatio * tiltStrength * yRatio * yRatio;
    
    setLocalMouse({ x, y });
  };

  return (
    <div
      ref={cardRef}
      className={`group cursor-pointer transition-all duration-700 ${
        contentStage === 'entering' 
          ? 'opacity-0 transform translate-y-8' 
          : 'opacity-100 transform translate-y-0'
      } ${isActive ? 'z-40' : 'z-20'}`} // Aggressive Z-index for active card
      style={{
        transitionDelay: contentStage === 'visible' ? `${index * 200}ms` : '0ms',
        // The mathematically sound 3D transformation
        transform: `perspective(1000px) rotateY(${localMouse.x * -0.3}deg) rotateX(${localMouse.y * 0.3}deg) scale3d(1, 1, 1)`
      }}
      onClick={() => onClick(service.id)}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setLocalMouse({ x: 0, y: 0 })}
    >
      {/* Hypercube Outer Shell */}
      <div className={`p-0.5 rounded-xl transition-all duration-500 group-hover:scale-[1.03] ${
        isActive ? 'scale-[1.03] shadow-2xl shadow-cyan-500/10' : 'shadow-xl'
      }`} style={{
          background: `linear-gradient(to right bottom, ${service.gradient.split(' ')[1]}, ${service.gradient.split(' ')[2]})`
      }}>
        <div className={`bg-slate-950/90 backdrop-blur-md rounded-xl p-6 h-full border border-slate-800 transition-all duration-500 relative overflow-hidden`}>
          
          {/* Hypercube Inner Borders (Fractal Geometry) */}
          <div className="hypercube-border">
              <style jsx>{`
                  .hypercube-border::before { border-color: ${getPlasmaColor(service.gradient).replace('0.5', '0.1')}; }
                  .hypercube-border::after { border-color: ${getPlasmaColor(service.gradient).replace('0.5', '0.05')}; }
              `}</style>
          </div>
          
          {/* Holographic Glow Effect (Reacts to Mouse) */}
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: `radial-gradient(circle at ${localMouse.x * 2 + 50}% ${localMouse.y * 2 + 50}%, ${service.particleColor.replace('0.1', '0.2')}, transparent 50%)`
            }}
          />
          
          {/* Holographic Plasma Core (Pulsing when Active) */}
          {isActive && (
              <div 
                  className="absolute top-1/2 left-1/2 w-16 h-16 transform -translate-x-1/2 -translate-y-1/2 plasma-core opacity-70"
                  style={{
                      '--plasma-color': getPlasmaColor(service.gradient),
                      background: getPlasmaColor(service.gradient).replace('0.5', '0.8')
                  }}
              />
          )}

          {/* Scanline Overlay (Terminal Analysis Look) */}
          {isActive && <div className="scanline-overlay"></div>}
          
          {/* Node Header */}
          <div className="relative z-10 flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className={`text-4xl font-light bg-gradient-to-br ${service.gradient} bg-clip-text text-transparent`}>
                {service.icon}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-1 font-mono">{service.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
              </div>
            </div>
            {/* Collapse/Expand Icon */}
            <div className={`transform transition-transform duration-300 ${isActive ? 'rotate-180' : ''}`}>
              <svg className="w-6 h-6 text-slate-400 group-hover:text-cyan-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Stat Readout */}
          <div className="relative z-10 flex gap-4 mb-6 pt-4 border-t border-slate-800">
            {Object.entries(service.stats).map(([key, value]) => (
              <div key={key} className="text-left">
                <div className={`text-xl font-bold bg-gradient-to-br ${service.gradient} bg-clip-text text-transparent`}>
                  {value}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
                  {key}
                </div>
              </div>
            ))}
          </div>

          {/* Expanded Content - Structured Terminal View */}
          <div 
              className="relative z-10 transition-all duration-500 overflow-hidden"
              style={{
                  maxHeight: isActive ? '1000px' : '0', // Use max-height for fluid transition
                  paddingTop: isActive ? '1rem' : '0'
              }}
          >
            {isActive && (
              <div className="space-y-6 pt-4 border-t border-slate-800">
                  {/* Tabbed Navigation for Data Structure */}
                  <div className="flex border-b border-slate-700/50">
                      <button 
                          onClick={() => setActiveTab('features')}
                          className={`px-4 py-2 text-sm font-mono transition-colors ${
                              activeTab === 'features' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-500 hover:text-slate-300'
                          }`}
                      >
                          Feature Set
                      </button>
                      <button 
                          onClick={() => setActiveTab('capabilities')}
                          className={`px-4 py-2 text-sm font-mono transition-colors ${
                              activeTab === 'capabilities' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-500 hover:text-slate-300'
                          }`}
                      >
                          Capabilities
                      </button>
                      <button 
                          onClick={() => setActiveTab('techStack')}
                          className={`px-4 py-2 text-sm font-mono transition-colors ${
                              activeTab === 'techStack' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-500 hover:text-slate-300'
                          }`}
                      >
                          Dependencies
                      </button>
                  </div>
                  
                  {/* Tab Content */}
                  <div className="space-y-4">
                      {activeTab === 'features' && (
                          <ul className="list-none space-y-2">
                              {service.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                                      <span className="text-cyan-400 font-bold">»</span>
                                      <span>{feature}</span>
                                  </li>
                              ))}
                          </ul>
                      )}
                      
                      {activeTab === 'capabilities' && (
                          <ul className="list-none space-y-2">
                              {service.capabilities.map((capability, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                                      <span className="text-cyan-400 font-bold">»</span>
                                      <span>{capability}</span>
                                  </li>
                              ))}
                          </ul>
                      )}

                      {activeTab === 'techStack' && (
                          <div className="flex flex-wrap gap-2">
                              {service.techStack.map((tech, idx) => (
                                  <span 
                                      key={tech}
                                      className="px-3 py-1 text-xs bg-slate-800/50 text-slate-400 rounded-lg border border-slate-700/50 hover:border-cyan-400 transition-colors duration-300 font-mono"
                                  >
                                      {tech}
                                  </span>
                              ))}
                          </div>
                      )}
                  </div>

                {/* Case Study Preview */}
                <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/50">
                  <h4 className="text-slate-400 text-sm font-mono mb-2">IMPACT STORY</h4>
                  <p className="text-slate-300 text-sm">{service.caseStudy}</p>
                </div>

                {/* Action Button */}
                <button className={`w-full py-3 rounded-xl bg-gradient-to-r ${service.gradient} text-white font-mono text-sm tracking-wide hover:shadow-lg transition-all duration-300 transform hover:scale-[1.01] flex items-center justify-center gap-2`}>
                  <span>ENTER ANALYSIS MODE</span>
                  <div className="group-hover:translate-x-1 transition-transform duration-200">→</div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Tech Stack Marquee Component (Retained)
const TechStackMarquee = () => {
  const techStack = [
    "React", "Next.js", "TypeScript", "Node.js", "Python", "TensorFlow", 
    "PyTorch", "Kubernetes", "Docker", "AWS", "GCP", "Azure", "GraphQL",
    "PostgreSQL", "MongoDB", "Redis", "Kafka", "Elasticsearch", "Three.js",
    "WebGL", "WebRTC", "WebAssembly", "Rust", "Go", "Swift", "Kotlin"
  ];

  return (
    <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
      <div className="flex animate-marquee whitespace-nowrap">
        {techStack.concat(techStack).map((tech, index) => (
          <div
            key={`${tech}-${index}`}
            className="mx-4 px-6 py-3 bg-slate-800/50 backdrop-blur-sm rounded-full border border-slate-700/50 text-slate-300 font-mono text-sm hover:bg-slate-700/50 hover:border-cyan-400/30 hover:text-cyan-300 transition-all duration-300 cursor-default"
          >
            {tech}
          </div>
        ))}
      </div>
      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
};

// Footer Component (Retained)
const Footer = ({ onNavigate }) => {
  const [currentYear] = useState(new Date().getFullYear());

  const quickLinks = [
    { name: 'What We Do', page: 'what-we-do' },
    { name: 'About Us', page: 'about' },
    { name: 'Articles', page: 'articles' },
    { name: 'Contact', page: 'contact' }
  ];

  const handleNavigation = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  return (
    <footer className="relative border-t border-slate-800 bg-slate-950/50 backdrop-blur-xl">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/20 pointer-events-none"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
              <div className="text-white font-mono text-lg font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                NIXQUE
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mb-6">
              Building the future through innovative technology solutions. 
              We create digital experiences that transform businesses and empower growth.
            </p>
            
            {/* Social Links (Placeholder) */}
            <div className="flex gap-3">
               <span className="text-slate-500 text-xs font-mono">SOCIAL LINKS PENDING</span>
            </div>
          </div>

          <div>
            <h3 className="text-white font-mono text-sm font-semibold mb-4 uppercase tracking-wider">
              Navigation
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => handleNavigation(link.page)}
                    className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-300 font-mono hover:underline cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-mono text-sm font-semibold mb-4 uppercase tracking-wider">
              Connect
            </h3>
            <div className="space-y-2 text-sm">
              <a
                href="mailto:contact@nixque.com"
                className="block text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-mono"
              >
                contact@nixque.com
              </a>
              <a
                href="tel:+254788979327"
                className="block text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-mono"
              >
                +2547 42 606971
              </a>
              <p className="text-slate-400 font-mono">
                Nairobi | Kisumu, Kenya
              </p>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-slate-500 text-sm font-mono">
              © {currentYear} NIXQUE LTD. All rights reserved.
            </div>

            <div className="flex gap-6 text-sm">
              <button
                onClick={() => handleNavigation('privacy')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Privacy
              </button>
              <button
                onClick={() => handleNavigation('terms')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Terms
              </button>
              <button
                onClick={() => handleNavigation('cookies')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SystemicCreationPage;